import { useMemo } from "react";
import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApiConfig } from "@/providers/ApiConfigProvider";
import { graphqlQuery } from "@/lib/api/graphql";
import { usePaymentStatusChanges } from "./usePaymentStatusChanges";
import { generateMockTransactions } from "@/lib/mock-transactions";
import type { Transaction, TransactionType, TransactionStatus } from "@/types/transaction";
import type {
  TransactionsPaginationApi,
  CustomerTransactionsSummaryApi,
  GetCustomerTransactionsParams,
} from "@/types/api/transactions";
import type { GetTransactionsParams } from "@/lib/api/transactions";
import type { PaymentStatusChange } from "@/types/api/payment-status";
import {
  type GraphQLConnection,
  pageToCursor,
  buildGraphQLSortVariable as buildSort,
  computeMockPagination,
  createDefaultPagination,
  computePaginationWithCount,
} from "@/lib/graphql-pagination";

// --- GraphQL node type ---

export interface TransactionGraphQLNode {
  transactionId: number;
  referenceNumber: string | null;
  customerId: number;
  customerName: string | null;
  accountNumber: string | null;
  transactionDate: string;
  dueDate: string | null;
  amount: number;
  netAmount: number | null;
  vatAmount: number | null;
  outstandingAmount: number;
  description: string | null;
  transactionNotes: string | null;
  headerReference: string | null;
  accountCode: string | null;
  onDispute: boolean;
  hasDispute: boolean;
  subLedger: string | null;
  subLedgerName: string | null;
  orderNumber: string | null;
  documentUrl: string | null;
  transactionReference: string | null;
  bankReference: string | null;
  currencyAmount: number | null;
  currencyNetAmount: number | null;
  currencyVatAmount: number | null;
  currencyOutstandingAmount: number | null;
  displayAmount: number | null;
  displayNetAmount: number | null;
  displayVatAmount: number | null;
  displayOutstandingAmount: number | null;
  daysOverdue: number;
  transactionCreatedAt: string;
  transactionModifiedAt: string | null;
  currencyCode: string | null;
  transactionTypeCode: string | null;
  transactionTypeName: string | null;
  transactionStatusCode: string | null;
  transactionStatusName: string | null;
  paymentMethodName: string | null;
  createdByName: string | null;
  modifiedByName: string | null;
  ageingBucket: string | null;
  totalAllocated: number;
  allocationCount: number;
  allocationRef: string | null;
  allocationStatusCode: string | null;
  allocationStatusName: string | null;
  hasPromise: boolean;
  hasCase: boolean;
  companyName: string | null;
}

export interface TransactionListGraphQLResponse {
  transactions: {
    transactionList: GraphQLConnection<TransactionGraphQLNode>;
  };
}

interface TransactionCountGraphQLResponse {
  transactions: {
    transactionCount: number;
  };
}

export interface TransactionTotalsData {
  totalCount: number;
  totalAmount: number;
  totalOutstanding: number;
  overdueCount: number;
  overdueAmount: number;
  atRiskAmount: number;
  invoiceCount: number;
  receiptCount: number;
  creditNoteCount: number;
  unallocatedReceiptsCount: number;
  unallocatedReceiptsAmount: number;
  totalCurrencyOutstanding: number;
  overdueCurrencyAmount: number;
  currencyAtRiskAmount: number;
  currencyUnallocatedReceiptsAmount: number;
}

interface TransactionTotalsGraphQLResponse {
  transactions: {
    transactionTotals: TransactionTotalsData;
  };
}

// --- GraphQL queries ---

export const TRANSACTION_LIST_QUERY = `
  query TransactionList($first: Int, $after: String, $sort: [TransactionListSortSortByInput!], $filter: TransactionListFilterInput) {
    transactions {
      transactionList(first: $first, after: $after, sort: $sort, filter: $filter) {
        edges {
          node {
            transactionId
            referenceNumber
            customerId
            customerName
            accountNumber
            transactionDate
            dueDate
            amount
            netAmount
            vatAmount
            outstandingAmount
            description
            transactionNotes
            headerReference
            accountCode
            onDispute
            hasDispute
            subLedger
            subLedgerName
            orderNumber
            documentUrl
            transactionReference
            bankReference
            currencyAmount
            currencyNetAmount
            currencyVatAmount
            currencyOutstandingAmount
            displayAmount
            displayNetAmount
            displayVatAmount
            displayOutstandingAmount
            daysOverdue
            transactionCreatedAt
            transactionModifiedAt
            currencyCode
            transactionTypeCode
            transactionTypeName
            transactionStatusCode
            transactionStatusName
            paymentMethodName
            createdByName
            modifiedByName
            ageingBucket
            totalAllocated
            allocationCount
            allocationRef
            allocationStatusCode
            allocationStatusName
            hasPromise
            hasCase
            companyName
          }
        }
        pageInfo {
          startCursor
          endCursor
          hasNextPage
          hasPreviousPage
        }
      }
    }
  }
`;

const TRANSACTION_COUNT_QUERY = `
  query TransactionCount($filter: TransactionListFilterInput) {
    transactions {
      transactionCount(filter: $filter)
    }
  }
`;

const TRANSACTION_TOTALS_QUERY = `
  query TransactionTotals($filter: TransactionListFilterInput) {
    transactions {
      transactionTotals(filter: $filter) {
        totalCount
        totalAmount
        totalOutstanding
        overdueCount
        overdueAmount
        atRiskAmount
        invoiceCount
        receiptCount
        creditNoteCount
        unallocatedReceiptsCount
        unallocatedReceiptsAmount
        totalCurrencyOutstanding
        overdueCurrencyAmount
        currencyAtRiskAmount
        currencyUnallocatedReceiptsAmount
      }
    }
  }
`;

const CUSTOMER_METRIC_BASE_TOTALS_QUERY = `
  query CustomerMetricBaseTotals($filter: TransactionListFilterInput) {
    transactions {
      transactionTotals(filter: $filter) {
        totalOutstanding
        overdueAmount
        totalCount
        overdueCount
      }
    }
  }
`;

const CUSTOMER_METRIC_CURRENCY_TOTALS_QUERY = `
  query CustomerMetricCurrencyTotals($filter: TransactionListFilterInput) {
    transactions {
      transactionTotals(filter: $filter) {
        totalCurrencyOutstanding
        overdueCurrencyAmount
      }
    }
  }
`;

interface CustomerMetricBaseTotalsGraphQLResponse {
  transactions: {
    transactionTotals: { totalOutstanding: number; overdueAmount: number; totalCount: number; overdueCount: number };
  };
}

interface CustomerMetricCurrencyTotalsGraphQLResponse {
  transactions: {
    transactionTotals: { totalCurrencyOutstanding: number; overdueCurrencyAmount: number };
  };
}

export interface CustomerMetricTotals {
  baseOutstanding: number;
  baseOverdue: number;
  currencyOutstanding: number;
  currencyOverdue: number;
  outstandingCount: number;
  overdueCount: number;
}

// --- Mapping ---

function mapTransactionTypeCode(code: string | null): TransactionType {
  const typeMap: Record<string, TransactionType> = {
    invoice: "invoice",
    credit_note: "credit_note",
    receipt: "receipt",
    debit_adjustment: "debit_adjustment",
    credit_adjustment: "credit_adjustment",
  };
  return typeMap[(code ?? "").toLowerCase()] ?? "invoice";
}

function mapTransactionStatusCode(code: string | null): TransactionStatus {
  const statusMap: Record<string, TransactionStatus> = {
    open: "outstanding",
    paid: "completed",
    overdue: "overdue",
  };
  return statusMap[(code ?? "").toLowerCase()] ?? "outstanding";
}

const uiStatusToDbStatus: Record<string, string> = {
  outstanding: "Open",
  completed: "Paid",
  overdue: "Overdue",
};

function mapUiStatusToDbStatus(uiStatus: string): string {
  return uiStatusToDbStatus[uiStatus] ?? uiStatus;
}

export function mapGraphQLNodeToTransaction(node: TransactionGraphQLNode): Transaction {
  return {
    id: node.transactionId,
    referenceNumber: node.referenceNumber || String(node.transactionId),
    transactionDate: node.transactionDate,
    customerId: node.customerId,
    customerName: node.customerName ?? "",
    transactionType: mapTransactionTypeCode(node.transactionTypeCode),
    amount: node.amount,
    currency: node.currencyCode ?? "GBP",
    status: mapTransactionStatusCode(node.transactionStatusCode),
    description: node.description ?? "",
    notes: node.transactionNotes ?? undefined,
    createdBy: node.createdByName ?? "",
    modifiedBy: node.modifiedByName ?? undefined,
    createdAt: node.transactionCreatedAt,
    modifiedAt: node.transactionModifiedAt ?? undefined,
    dueDate: node.dueDate ?? undefined,
    headerReference: node.headerReference ?? undefined,
    paymentMethod: node.paymentMethodName ?? undefined,
    documentUrl: node.documentUrl ?? undefined,
    outstandingAmount: node.outstandingAmount,
    onDispute: node.onDispute,
    hasDispute: node.hasDispute,
    accountCode: node.accountCode ?? undefined,
    netAmount: node.netAmount ?? undefined,
    vatAmount: node.vatAmount ?? undefined,
    subLedger: node.subLedger ?? undefined,
    subLedgerName: node.subLedgerName ?? undefined,
    orderNumber: node.orderNumber ?? undefined,
    transactionReference: node.transactionReference ?? undefined,
    bankReference: node.bankReference ?? undefined,
    currencyGross: node.currencyAmount ?? undefined,
    currencyNet: node.currencyNetAmount ?? undefined,
    currencyVAT: node.currencyVatAmount ?? undefined,
    currencyOutstanding: node.currencyOutstandingAmount ?? undefined,
    displayAmount: node.displayAmount ?? undefined,
    displayNetAmount: node.displayNetAmount ?? undefined,
    displayVatAmount: node.displayVatAmount ?? undefined,
    displayOutstandingAmount: node.displayOutstandingAmount ?? undefined,
    accountNumber: node.accountNumber ?? undefined,
    daysOverdue: node.daysOverdue,
    ageingBucket: node.ageingBucket ?? "",
    hasPromise: node.hasPromise,
    hasCase: node.hasCase,
    allocationStatusCode: node.allocationStatusCode ?? "",
    allocationStatusName: node.allocationStatusName ?? "",
    totalAllocated: node.totalAllocated,
    allocationCount: node.allocationCount,
    allocationRef: node.allocationRef ?? undefined,
    companyName: node.companyName ?? undefined,
  };
}

// --- Sort / Filter builders ---

export const sortFieldMap: Record<string, string> = {
  referenceNumber: "ReferenceNumber",
  transactionDate: "TransactionDate",
  dueDate: "DueDate",
  customerName: "CustomerName",
  transactionType: "TransactionTypeName",
  amount: "Amount",
  outstandingAmount: "OutstandingAmount",
  status: "TransactionStatusCode",
  currency: "CurrencyCode",
  description: "Description",
  createdBy: "CreatedByName",
  modifiedBy: "ModifiedByName",
  accountCode: "AccountCode",
  netAmount: "NetAmount",
  vatAmount: "VatAmount",
  onDispute: "OnDispute",
  subLedger: "SubLedger",
  orderNumber: "OrderNumber",
  displayAmount: "DisplayAmount",
  displayNetAmount: "DisplayNetAmount",
  displayVatAmount: "DisplayVatAmount",
  displayOutstandingAmount: "DisplayOutstandingAmount",
};

function buildGraphQLSortVariable(sortBy?: string, sortOrder?: "asc" | "desc") {
  return buildSort(sortFieldMap, sortBy, sortOrder);
}

export function buildGraphQLFilterVariable(params: GetTransactionsParams) {
  const filter: Record<string, unknown> = {};

  if (params.search) {
    filter.summary = params.search;
  }
  if (params.transactionType) {
    const types = params.transactionType.split(",");
    if (types.length === 1) {
      filter.transactionTypeCode = { eq: types[0] };
    } else {
      filter.transactionTypeCode = { containsValues: types };
    }
  }
  if (params.excludeTransactionTypes) {
    const excludeTypes = params.excludeTransactionTypes.split(",");
    filter.transactionTypeCode = {
      ...(filter.transactionTypeCode as Record<string, unknown> | undefined),
      notContainsValues: excludeTypes,
    };
  }
  if (params.status) {
    const statuses = params.status.split(",").map(mapUiStatusToDbStatus);
    if (statuses.length === 1) {
      filter.transactionStatusCode = { eq: statuses[0] };
    } else {
      filter.transactionStatusCode = { containsValues: statuses };
    }
  }
  if (params.customerId) {
    const ids = params.customerId.split(",").map(Number);
    if (ids.length === 1) {
      filter.customerId = { eq: ids[0] };
    } else {
      filter.customerId = { containsValues: ids };
    }
  }
  if (params.currency) {
    const currencies = params.currency.split(",");
    if (currencies.length === 1) {
      filter.currencyCode = { eq: currencies[0] };
    } else {
      filter.currencyCode = { containsValues: currencies };
    }
  }
  if (params.dateFrom || params.dateTo) {
    const dateFilter: Record<string, string> = {};
    if (params.dateFrom) dateFilter.ge = params.dateFrom;
    if (params.dateTo) dateFilter.le = params.dateTo;
    filter.transactionDate = dateFilter;
  }
  const hasCurrencyFilter = !!params.currency && !params.currency.includes(",");
  if (params.amountMin !== undefined || params.amountMax !== undefined) {
    const amountFilter: Record<string, number> = {};
    if (params.amountMin !== undefined) amountFilter.ge = params.amountMin;
    if (params.amountMax !== undefined) amountFilter.le = params.amountMax;
    const amountField = hasCurrencyFilter ? "currencyAmount" : "amount";
    filter[amountField] = amountFilter;
  }
  if (params.outstandingNotZero) {
    const outstandingField = hasCurrencyFilter ? "currencyOutstandingAmount" : "outstandingAmount";
    filter[outstandingField] = { ne: 0 };
  } else if (params.outstandingMin !== undefined || params.outstandingMax !== undefined) {
    const outstandingFilter: Record<string, number> = {};
    if (params.outstandingMin !== undefined) outstandingFilter.ge = params.outstandingMin;
    if (params.outstandingMax !== undefined) outstandingFilter.le = params.outstandingMax;
    const outstandingField = hasCurrencyFilter ? "currencyOutstandingAmount" : "outstandingAmount";
    filter[outstandingField] = outstandingFilter;
  }
  if (params.hasCase !== undefined) {
    filter.hasCase = { eq: params.hasCase };
  }
  if (params.hasPromise !== undefined) {
    filter.hasPromise = { eq: params.hasPromise };
  }
  if (params.onDispute !== undefined) {
    filter.hasDispute = { eq: params.onDispute };
  }
  if (params.externalDispute !== undefined) {
    filter.onDispute = { eq: params.externalDispute };
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

/**
 * Build GraphQL filter for metric card clicks (Option A - frontend mapping).
 * Translates metricFilter string into property-based filters.
 */
export function buildMetricFilter(metricFilter: string | undefined): Record<string, unknown> | undefined {
  if (!metricFilter) return undefined;

  switch (metricFilter) {
    case "outstanding":
      return {
        transactionTypeCode: { eq: "invoice" },
        transactionStatusCode: { containsValues: ["Open", "Overdue"] },
      };
    case "overdue":
      return {
        transactionStatusCode: { eq: "Overdue" },
      };
    case "at-risk": {
      const sevenDaysFromNow = new Date();
      sevenDaysFromNow.setDate(sevenDaysFromNow.getDate() + 7);
      const dueDateStr = sevenDaysFromNow.toISOString().split("T")[0];
      return {
        transactionTypeCode: { eq: "invoice" },
        outstandingAmount: { gt: 0 },
        dueDate: { le: dueDateStr },
      };
    }
    case "unallocated-receipts":
      return {
        transactionTypeCode: { eq: "receipt" },
        outstandingAmount: { gt: 0 },
      };
    default:
      return undefined;
  }
}

/**
 * Merges the regular filter with the metric filter.
 * Metric filter properties take precedence (they override any conflicting property filters).
 */
export function mergeFilters(
  regularFilter: Record<string, unknown> | undefined,
  metricFilter: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!regularFilter && !metricFilter) return undefined;
  if (!regularFilter) return metricFilter;
  if (!metricFilter) return regularFilter;
  return { ...regularFilter, ...metricFilter };
}

// --- Mock helpers ---

function getMockTransactions(): Transaction[] {
  return generateMockTransactions();
}

// --- Payment status enrichment ---

function mergePaymentStatuses(
  transactions: Transaction[],
  statusMap: Map<string, PaymentStatusChange> | undefined,
): Transaction[] {
  if (!statusMap || statusMap.size === 0) return transactions;
  return transactions.map((x) => {
    const change = statusMap.get(x.referenceNumber);
    if (change) {
      return { ...x, paymentLinkStatus: change.status };
    }
    return x;
  });
}

// --- Result types ---

export interface TransactionsListResult {
  transactions: Transaction[];
  pagination: TransactionsPaginationApi;
}

// --- Hooks ---

function useTransactionCountGraphQL(params?: GetTransactionsParams) {
  const apiConfig = useApiConfig();
  const regularFilter = buildGraphQLFilterVariable(params ?? {});
  const metricFilterObj = buildMetricFilter(params?.metricFilter);
  const filter = mergeFilters(regularFilter, metricFilterObj);

  return useModeAwareQuery<number, Error>(
    ["transactions-graphql-count", filter],
    async ({ signal }) => {
      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<TransactionCountGraphQLResponse>(
        apiConfig,
        TRANSACTION_COUNT_QUERY,
        variables,
        signal,
      );

      return response.transactions.transactionCount;
    },
    0,
    {
      staleTime: 30_000,
      refetchOnMount: "always",
    },
  );
}

export function useTransactionsListGraphQL(params?: GetTransactionsParams) {
  const apiConfig = useApiConfig();
  const mockTransactions = getMockTransactions();
  const queryParams: GetTransactionsParams = { page: 1, pageSize: 25, sortBy: "transactionDate", sortOrder: "desc", ...params };
  const mockPage = queryParams.page ?? 1;
  const mockPageSize = queryParams.pageSize ?? 25;
  const mockPaged = mockTransactions.slice((mockPage - 1) * mockPageSize, mockPage * mockPageSize);
  const mockPagination = computeMockPagination(mockTransactions.length, mockPage, mockPageSize);
  const mockResult: TransactionsListResult = { transactions: mockPaged, pagination: mockPagination };

  return useModeAwareQuery<TransactionsListResult, Error>(
    ["transactions-graphql-list", queryParams],
    async ({ signal }) => {
      const page = queryParams.page ?? 1;
      const pageSize = queryParams.pageSize ?? 25;

      const regularFilter = buildGraphQLFilterVariable(queryParams);
      const metricFilterObj = buildMetricFilter(queryParams.metricFilter);
      const filter = mergeFilters(regularFilter, metricFilterObj);

      const variables: Record<string, unknown> = {
        first: pageSize,
        sort: buildGraphQLSortVariable(queryParams.sortBy, queryParams.sortOrder),
        filter,
      };

      if (page > 1) {
        variables.after = pageToCursor(page);
      }

      const response = await graphqlQuery<TransactionListGraphQLResponse>(
        apiConfig,
        TRANSACTION_LIST_QUERY,
        variables,
        signal,
      );

      const connection = response.transactions.transactionList;
      const transactions = connection.edges.map((x) => mapGraphQLNodeToTransaction(x.node));

      const pagination: TransactionsPaginationApi = {
        page,
        pageSize,
        totalItems: transactions.length,
        totalPages: 1,
        hasNextPage: connection.pageInfo.hasNextPage,
        hasPreviousPage: connection.pageInfo.hasPreviousPage,
      };

      return { transactions, pagination };
    },
    mockResult,
    {
      staleTime: 0,
      refetchOnMount: "always",
    },
  );
}

export function useTransactionTotalsGraphQL(params?: GetTransactionsParams) {
  const apiConfig = useApiConfig();
  const regularFilter = buildGraphQLFilterVariable(params ?? {});
  const metricFilterObj = buildMetricFilter(params?.metricFilter);
  const filter = mergeFilters(regularFilter, metricFilterObj);

  const allMockTransactions = getMockTransactions();
  // When a single currency is selected in the filter, scope mock totals to that currency only
  const selectedCurrencies = params?.currency ? params.currency.split(",").map((c) => c.trim()).filter(Boolean) : [];
  const mockTransactions = selectedCurrencies.length > 0
    ? allMockTransactions.filter((x) => selectedCurrencies.includes(x.currency))
    : allMockTransactions;
  const mockTotals: TransactionTotalsData = {
    totalCount: mockTransactions.length,
    totalAmount: mockTransactions.reduce((sum, x) => sum + x.amount, 0),
    totalOutstanding: mockTransactions.reduce((sum, x) => sum + (x.outstandingAmount ?? 0), 0),
    overdueCount: mockTransactions.filter((x) => x.status === "overdue").length,
    overdueAmount: mockTransactions.filter((x) => x.status === "overdue").reduce((sum, x) => sum + (x.outstandingAmount ?? 0), 0),
    atRiskAmount: mockTransactions.filter((x) => x.transactionType === "invoice" && (x.outstandingAmount ?? 0) > 0 && x.dueDate != null).reduce((sum, x) => sum + (x.outstandingAmount ?? 0), 0),
    invoiceCount: mockTransactions.filter((x) => x.transactionType === "invoice").length,
    receiptCount: mockTransactions.filter((x) => x.transactionType === "receipt").length,
    creditNoteCount: mockTransactions.filter((x) => x.transactionType === "credit_note").length,
    unallocatedReceiptsCount: mockTransactions.filter((x) => x.transactionType === "receipt" && (x.outstandingAmount ?? 0) > 0).length,
    unallocatedReceiptsAmount: mockTransactions.filter((x) => x.transactionType === "receipt" && (x.outstandingAmount ?? 0) > 0).reduce((sum, x) => sum + (x.outstandingAmount ?? 0), 0),
    totalCurrencyOutstanding: mockTransactions.reduce((sum, x) => sum + (x.outstandingAmount ?? 0), 0),
    overdueCurrencyAmount: mockTransactions.filter((x) => x.status === "overdue").reduce((sum, x) => sum + (x.outstandingAmount ?? 0), 0),
    currencyAtRiskAmount: mockTransactions.filter((x) => x.transactionType === "invoice" && (x.outstandingAmount ?? 0) > 0 && x.dueDate != null).reduce((sum, x) => sum + (x.outstandingAmount ?? 0), 0),
    currencyUnallocatedReceiptsAmount: mockTransactions.filter((x) => x.transactionType === "receipt" && (x.outstandingAmount ?? 0) !== 0).reduce((sum, x) => sum + (x.outstandingAmount ?? 0), 0),
  };

  return useModeAwareQuery<TransactionTotalsData, Error>(
    ["transactions-graphql-totals", filter],
    async ({ signal }) => {
      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<TransactionTotalsGraphQLResponse>(
        apiConfig,
        TRANSACTION_TOTALS_QUERY,
        variables,
        signal,
      );

      return response.transactions.transactionTotals;
    },
    mockTotals,
    {
      staleTime: 30_000,
      refetchOnMount: "always",
    },
  );
}

// --- Composite page data hook ---

const defaultPagination: TransactionsPaginationApi = createDefaultPagination(25);

const defaultTotals: TransactionTotalsData = {
  totalCount: 0,
  totalAmount: 0,
  totalOutstanding: 0,
  overdueCount: 0,
  overdueAmount: 0,
  atRiskAmount: 0,
  invoiceCount: 0,
  receiptCount: 0,
  creditNoteCount: 0,
  unallocatedReceiptsCount: 0,
  unallocatedReceiptsAmount: 0,
  totalCurrencyOutstanding: 0,
  overdueCurrencyAmount: 0,
  currencyAtRiskAmount: 0,
  currencyUnallocatedReceiptsAmount: 0,
};

/**
 * Composite hook for the transactions page.
 * Combines list, count, and totals queries into a single return value.
 * Also enriches transactions with payment link status.
 */
export function useTransactionsPageDataGraphQL(params?: GetTransactionsParams) {
  const listQuery = useTransactionsListGraphQL(params);
  const countQuery = useTransactionCountGraphQL(params);
  // Totals query runs without metric filter to get global KPIs for dashboard cards
  const totalsParams: GetTransactionsParams = { ...params };
  delete totalsParams.metricFilter;
  const totalsQuery = useTransactionTotalsGraphQL(totalsParams);
  const { data: statusMap } = usePaymentStatusChanges();

  const basePagination = listQuery.data?.pagination ?? defaultPagination;
  const totalCount = countQuery.data ?? 0;
  const pagination = computePaginationWithCount(basePagination, totalCount, params?.pageSize);

  const transactions = useMemo(
    () => mergePaymentStatuses(listQuery.data?.transactions ?? [], statusMap),
    [listQuery.data, statusMap],
  );

  return {
    transactions,
    pagination,
    totals: totalsQuery.data ?? defaultTotals,
    isLoading: listQuery.isLoading,
    error: listQuery.error,
    refetch: () => {
      listQuery.refetch();
      countQuery.refetch();
      totalsQuery.refetch();
    },
  };
}

// --- Customer Transaction GraphQL hooks ---

/**
 * Build a GraphQL filter for customer transaction queries.
 * Maps customerId + GetCustomerTransactionsParams to the TransactionListFilterInput shape.
 */
function buildCustomerTransactionFilter(
  customerId: string | undefined,
  params?: GetCustomerTransactionsParams,
): Record<string, unknown> | undefined {
  const filter: Record<string, unknown> = {};

  if (customerId) {
    filter.customerId = { eq: Number(customerId) };
  }

  if (params?.search) {
    filter.summary = params.search;
  }

  if (params?.transactionType) {
    const types = params.transactionType.split(",");
    if (types.length === 1) {
      filter.transactionTypeCode = { eq: types[0] };
    } else {
      filter.transactionTypeCode = { containsValues: types };
    }
  }

  if (params?.outstandingStatus === "outstanding") {
    // Use != 0 instead of > 0 to include receipts/credit notes/credit adjustments
    // which have negative outstanding amounts.
    filter.outstandingAmount = { ne: 0 };
  } else if (params?.outstandingStatus === "completed") {
    filter.outstandingAmount = { eq: 0 };
  }

  if (params?.currency) {
    filter.currencyCode = { eq: params.currency };
  }

  if (params?.allCompanies) {
    filter.allCompanies = true;
  }

  if (params?.dateFrom || params?.dateTo) {
    const dateFilter: Record<string, string> = {};
    if (params.dateFrom) dateFilter.ge = params.dateFrom;
    if (params.dateTo) dateFilter.le = params.dateTo;
    filter.transactionDate = dateFilter;
  }

  if (params?.amountMin !== undefined || params?.amountMax !== undefined) {
    const amountFilter: Record<string, number> = {};
    if (params.amountMin !== undefined) amountFilter.ge = params.amountMin;
    if (params.amountMax !== undefined) amountFilter.le = params.amountMax;
    filter.amount = amountFilter;
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
}

export interface CustomerTransactionsGraphQLResult {
  transactions: Transaction[];
  pagination: TransactionsPaginationApi | null;
  summary: CustomerTransactionsSummaryApi | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch customer transactions via GraphQL with server-side pagination.
 * Drop-in replacement for useCustomerTransactionsPaginated from useTransactionsData.
 * Uses the same TransactionList rapid read query, filtered by customerId.
 */
export function useCustomerTransactionsPaginatedGraphQL(
  customerId: string | undefined,
  params?: GetCustomerTransactionsParams,
): CustomerTransactionsGraphQLResult {
  const apiConfig = useApiConfig();
  const mockTransactions = getMockTransactions();
  const outstandingStatus = params?.outstandingStatus;

  const queryParams: GetCustomerTransactionsParams = {
    page: 1,
    pageSize: 25,
    sortBy: "transactionDate",
    sortOrder: "desc",
    ...params,
  };

  const page = queryParams.page ?? 1;
  const pageSize = queryParams.pageSize ?? 25;

  const mockFiltered = useMemo(() => {
    const customerIdNum = customerId ? parseInt(customerId, 10) : undefined;
    return mockTransactions.filter((t) => {
      if (customerIdNum !== undefined && t.customerId !== customerIdNum) return false;
      if (outstandingStatus === "outstanding" && (t.outstandingAmount ?? 0) === 0) return false;
      if (outstandingStatus === "completed" && (t.outstandingAmount ?? 0) !== 0) return false;
      if (queryParams.currency && t.currency !== queryParams.currency) return false;
      return true;
    });
  }, [customerId, mockTransactions, outstandingStatus, queryParams.currency]);

  const mockPaged = mockFiltered.slice((page - 1) * pageSize, page * pageSize);
  const mockPagination = computeMockPagination(mockFiltered.length, page, pageSize);
  const mockListResult: TransactionsListResult = { transactions: mockPaged, pagination: mockPagination };

  const filter = buildCustomerTransactionFilter(customerId, queryParams);

  // --- List query ---
  const listQuery = useModeAwareQuery<TransactionsListResult, Error>(
    ["customer-transactions-graphql-list", customerId, queryParams],
    async ({ signal }) => {
      if (!customerId) {
        return { transactions: [], pagination: computeMockPagination(0, 1, pageSize) };
      }

      const variables: Record<string, unknown> = {
        first: pageSize,
        sort: buildGraphQLSortVariable(queryParams.sortBy, queryParams.sortOrder),
        filter,
      };

      if (page > 1) {
        variables.after = pageToCursor(page);
      }

      const response = await graphqlQuery<TransactionListGraphQLResponse>(
        apiConfig,
        TRANSACTION_LIST_QUERY,
        variables,
        signal,
      );

      const connection = response.transactions.transactionList;
      const transactions = connection.edges.map((x) => mapGraphQLNodeToTransaction(x.node));

      const pagination: TransactionsPaginationApi = {
        page,
        pageSize,
        totalItems: transactions.length,
        totalPages: 1,
        hasNextPage: connection.pageInfo.hasNextPage,
        hasPreviousPage: connection.pageInfo.hasPreviousPage,
      };

      return { transactions, pagination };
    },
    mockListResult,
    {
      enabled: !!customerId,
      staleTime: 0,
      refetchOnMount: "always",
    },
  );

  // --- Count query ---
  const countQuery = useModeAwareQuery<number, Error>(
    ["customer-transactions-graphql-count", customerId, filter],
    async ({ signal }) => {
      if (!customerId) return 0;

      const variables: Record<string, unknown> = {};
      if (filter) {
        variables.filter = filter;
      }

      const response = await graphqlQuery<TransactionCountGraphQLResponse>(
        apiConfig,
        TRANSACTION_COUNT_QUERY,
        variables,
        signal,
      );

      return response.transactions.transactionCount;
    },
    mockFiltered.length,
    {
      enabled: !!customerId,
      staleTime: 30_000,
      refetchOnMount: "always",
    },
  );

  // --- Totals query (for summary: totalOutstanding, totalOverdue) ---
  // Filtered by customerId only (no outstandingStatus) to reflect overall customer totals
  const totalsFilter = buildCustomerTransactionFilter(customerId, {
    allCompanies: params?.allCompanies,
  });
  const mockTotals: CustomerTransactionsSummaryApi = {
    totalTransactions: mockFiltered.length,
    totalOutstanding: mockFiltered.reduce((sum, t) => sum + (t.outstandingAmount ?? 0), 0),
    totalOverdue: mockFiltered.filter((t) => t.status === "overdue").reduce((sum, t) => sum + (t.outstandingAmount ?? 0), 0),
    totalOutstandingCustomer: 0,
    totalOverdueCustomer: 0,
  };

  const totalsQuery = useModeAwareQuery<CustomerTransactionsSummaryApi, Error>(
    ["customer-transactions-graphql-totals", customerId, totalsFilter],
    async ({ signal }) => {
      if (!customerId) return { totalTransactions: 0, totalOutstanding: 0, totalOverdue: 0, totalOutstandingCustomer: 0, totalOverdueCustomer: 0 };

      const variables: Record<string, unknown> = {};
      if (totalsFilter) {
        variables.filter = totalsFilter;
      }

      const response = await graphqlQuery<TransactionTotalsGraphQLResponse>(
        apiConfig,
        TRANSACTION_TOTALS_QUERY,
        variables,
        signal,
      );

      const totals = response.transactions.transactionTotals;
      return {
        totalTransactions: totals.totalCount,
        totalOutstanding: totals.totalOutstanding,
        totalOverdue: totals.overdueAmount,
        totalOutstandingCustomer: 0,
        totalOverdueCustomer: 0,
      };
    },
    mockTotals,
    {
      enabled: !!customerId,
      staleTime: 30_000,
      refetchOnMount: "always",
    },
  );

  // --- Combine results ---
  const basePagination = listQuery.data?.pagination ?? defaultPagination;
  const totalCount = countQuery.data ?? 0;
  const pagination = computePaginationWithCount(basePagination, totalCount, params?.pageSize);

  return {
    transactions: listQuery.data?.transactions ?? [],
    pagination,
    summary: totalsQuery.data ?? mockTotals,
    isLoading: listQuery.isLoading,
    error: listQuery.error,
    refetch: () => {
      listQuery.refetch();
      countQuery.refetch();
      totalsQuery.refetch();
    },
  };
}

/**
 * Hook to fetch outstanding transactions for a customer via GraphQL.
 * Drop-in replacement for useCustomerTransactions from useTransactionsData.
 */
export function useCustomerTransactionsGraphQL(
  customerId: string | undefined,
  params?: Omit<GetCustomerTransactionsParams, "outstandingStatus">,
) {
  return useCustomerTransactionsPaginatedGraphQL(customerId, {
    ...params,
    outstandingStatus: "outstanding",
  });
}

/**
 * Hook to fetch completed transactions for a customer via GraphQL.
 * Drop-in replacement for useCustomerCompletedTransactions from useTransactionsData.
 */
/**
 * Hook to fetch all metric card totals for a customer via two separate GraphQL requests.
 * Splitting into two requests ensures each runs in its own DI scope on the backend.
 * Outstanding filters use ne: 0 so negative balances (credits) are included.
 */
export function useCustomerMetricTotals(
  customerId: string | undefined,
  currencyCode: string | undefined,
  isConsolidatedView?: boolean,
): { data: CustomerMetricTotals; isLoading: boolean } {
  const apiConfig = useApiConfig();
  const mockTransactions = generateMockTransactions();
  const customerIdNum = customerId ? parseInt(customerId, 10) : undefined;

  const zeroTotals: CustomerMetricTotals = { baseOutstanding: 0, baseOverdue: 0, currencyOutstanding: 0, currencyOverdue: 0, outstandingCount: 0, overdueCount: 0 };

  const mockData: CustomerMetricTotals = useMemo(() => {
    const allTx = mockTransactions.filter(
      (t) => (customerIdNum === undefined || t.customerId === customerIdNum) &&
             (t.outstandingAmount ?? 0) !== 0,
    );
    const overdueTx = allTx.filter((t) => t.status === "overdue");
    const allCurrencyTx = !currencyCode ? [] : mockTransactions.filter(
      (t) => (customerIdNum === undefined || t.customerId === customerIdNum) &&
             t.currency === currencyCode &&
             (t.currencyOutstanding ?? 0) !== 0,
    );
    const currencyOverdueTx = allCurrencyTx.filter((t) => t.status === "overdue");
    return {
      baseOutstanding: allTx.reduce((sum, t) => sum + (t.outstandingAmount ?? 0), 0),
      baseOverdue: overdueTx.reduce((sum, t) => sum + (t.outstandingAmount ?? 0), 0),
      currencyOutstanding: allCurrencyTx.reduce((sum, t) => sum + (t.currencyOutstanding ?? 0), 0),
      currencyOverdue: currencyOverdueTx.reduce((sum, t) => sum + (t.currencyOutstanding ?? 0), 0),
      outstandingCount: allTx.length,
      overdueCount: overdueTx.length,
    };
  }, [customerIdNum, currencyCode, mockTransactions]);

  const query = useModeAwareQuery<CustomerMetricTotals, Error>(
    ["customer-metric-totals", customerId, currencyCode, isConsolidatedView],
    async ({ signal }) => {
      if (!customerId) return zeroTotals;

      const customerIdNum = Number(customerId);
      const baseFilter: Record<string, unknown> = { customerId: { eq: customerIdNum }, outstandingAmount: { ne: 0 } };
      if (isConsolidatedView) baseFilter.allCompanies = true;

      const baseRequest = graphqlQuery<CustomerMetricBaseTotalsGraphQLResponse>(
        apiConfig,
        CUSTOMER_METRIC_BASE_TOTALS_QUERY,
        { filter: baseFilter },
        signal,
      );

      const currencyRequest = currencyCode
        ? graphqlQuery<CustomerMetricCurrencyTotalsGraphQLResponse>(
            apiConfig,
            CUSTOMER_METRIC_CURRENCY_TOTALS_QUERY,
            { filter: {
              customerId: { eq: customerIdNum },
              currencyCode: { eq: currencyCode },
              currencyOutstandingAmount: { ne: 0 },
              ...(isConsolidatedView ? { allCompanies: true } : {}),
            } },
            signal,
          )
        : Promise.resolve(null);

      const [baseResponse, currencyResponse] = await Promise.all([baseRequest, currencyRequest]);

      return {
        baseOutstanding: baseResponse.transactions.transactionTotals.totalOutstanding,
        baseOverdue: baseResponse.transactions.transactionTotals.overdueAmount,
        currencyOutstanding: currencyResponse?.transactions.transactionTotals.totalCurrencyOutstanding ?? 0,
        currencyOverdue: currencyResponse?.transactions.transactionTotals.overdueCurrencyAmount ?? 0,
        outstandingCount: baseResponse.transactions.transactionTotals.totalCount,
        overdueCount: baseResponse.transactions.transactionTotals.overdueCount,
      };
    },
    mockData,
    { enabled: !!customerId, staleTime: 0, refetchOnMount: "always" },
  );

  return { data: query.data ?? zeroTotals, isLoading: query.isLoading };
}

export function useCustomerCompletedTransactionsGraphQL(
  customerId: string | undefined,
  params?: Omit<GetCustomerTransactionsParams, "outstandingStatus">,
) {
  return useCustomerTransactionsPaginatedGraphQL(customerId, {
    ...params,
    outstandingStatus: "completed",
  });
}
